/**
 * Logo Intro Animation — homepage only
 *
 * Interaction-driven: wheel / touch scrubs the animation forward or back.
 * On release, snaps to 0 (back to start) or 100% (into header) depending on
 * how far the user got. Any other interaction (click, keypress) or an
 * inactivity timer auto-completes the animation.
 */
(function () {
  'use strict';

  // Always on homepage: keep header pinned until user scrolls past the fold
  document.body.classList.add('logo-header-hold');
  window.addEventListener('scroll', function releaseHold() {
    if (window.scrollY >= window.innerHeight) {
      document.body.classList.remove('logo-header-hold');
      window.removeEventListener('scroll', releaseHold);
    }
  }, { passive: true });

  // Skip animation if already shown this session
  if (sessionStorage.getItem('logo-intro-seen')) return;

  var overlay    = document.getElementById('logo-intro-overlay');
  if (!overlay) return;

  var bg         = overlay.querySelector('.logo-intro-bg');
  var logoWrap   = overlay.querySelector('.logo-intro-logo');
  var lettersCon = overlay.querySelector('.logo-intro-letters');
  var headerEl   = document.querySelector('.header-logo-absolute');

  if (!bg || !logoWrap || !lettersCon || !headerEl) return;

  // Apply background image from theme setting (if provided)
  if (overlay.dataset.bgImage) {
    bg.style.backgroundImage = 'url(' + overlay.dataset.bgImage + ')';
    bg.classList.add('logo-intro-bg--image');
  }

  var letters = Array.from(lettersCon.querySelectorAll('.logo-intro-letter'));
  if (letters.length === 0) return;

  // Total virtual-scroll units to complete the animation
  var VIRTUAL_MAX         = 350;
  // Snap forward if released past this fraction, otherwise snap back
  var SNAP_THRESHOLD      = 0.4;
  // Snap animation duration — overridden by data-snap-duration (theme setting)
  var SNAP_DURATION       = parseInt(overlay.dataset.snapDuration, 10) || 2000;
  // Minimum backward-snap duration (ms)
  var SNAP_DURATION_MIN   = 400;
  // Trigger auto-complete after this many ms of no interaction — overridden by data-auto-delay
  var AUTO_TRIGGER_DELAY  = parseInt(overlay.dataset.autoDelay, 10) || 6000;
  // Max virtualY change allowed per single wheel or touchmove event.
  // Prevents one fast flick from skipping straight to the end via scrubbing.
  var DELTA_CAP           = 40;

  var virtualY              = 0;
  var deltas                = [];
  var targetDY              = 0;
  var done                  = false;
  var rafId                 = null;
  var interactionsReleased  = false;

  // Snap state
  var snapStart          = 0;
  var snapTarget         = 0;
  var snapStartTime      = null;
  var isSnapping         = false;
  var snapActiveDuration = 0;

  // Timers
  var autoTimer    = null;
  var wheelTimer   = null;

  // ---- Position maths ----

  function calcDeltas() {
    var hRect = headerEl.getBoundingClientRect();

    var startX = letters.map(function (el) {
      return el.getBoundingClientRect().left;
    });
    var widths = letters.map(function (el) {
      return el.getBoundingClientRect().width;
    });

    var totalLetterW = widths.reduce(function (a, b) { return a + b; }, 0);
    var endGapPx     = Math.max(0, (hRect.width - totalLetterW) / (letters.length - 1));

    var cursor = hRect.left;
    var endX = widths.map(function (w) {
      var pos = cursor;
      cursor += w + endGapPx;
      return pos;
    });

    deltas  = startX.map(function (sx, i) { return endX[i] - sx; });
    var lRect = logoWrap.getBoundingClientRect();
    targetDY = (hRect.top + hRect.height / 2) - (lRect.top + lRect.height / 2);
  }

  function ease(t) { return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; }
  function lerp(a, b, t) { return a + (b - a) * t; }

  // ---- Render ----

  function applyProgress(raw) {
    var p = ease(Math.min(1, Math.max(0, raw)));

    bg.style.opacity = String(Math.max(0, 0.8 - p));

    // Background is fully gone at p >= 0.8 — safe to restore scroll and clicks
    if (p >= 0.8) releaseInteractions();

    for (var i = 0; i < letters.length; i++) {
      letters[i].style.transform =
        'translateX(' + lerp(0, deltas[i], p) + 'px)';
    }

    logoWrap.style.transform =
      'translateY(calc(-50% + ' + lerp(0, targetDY, p) + 'px))';
  }

  // Manual-scrub frame
  function renderScrub() {
    rafId = null;
    if (done) return;
    applyProgress(virtualY / VIRTUAL_MAX);
    if (virtualY >= VIRTUAL_MAX) finish();
  }

  // Snap animation frame
  function renderSnap(timestamp) {
    if (done) { rafId = null; return; }

    if (snapStartTime === null) snapStartTime = timestamp;

    var elapsed = timestamp - snapStartTime;
    var t       = Math.min(1, elapsed / snapActiveDuration);

    virtualY = lerp(snapStart, snapTarget, ease(t));
    applyProgress(virtualY / VIRTUAL_MAX);

    if (t >= 1) {
      rafId    = null;
      virtualY = snapTarget;
      isSnapping = false;
      if (snapTarget >= VIRTUAL_MAX) finish();
    } else {
      rafId = requestAnimationFrame(renderSnap);
    }
  }

  function scheduleScrub() {
    if (!rafId) rafId = requestAnimationFrame(renderScrub);
  }

  function startSnap(target) {
    if (done) return;
    if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
    isSnapping    = true;
    snapStart     = virtualY;
    snapTarget    = target;
    snapStartTime = null;
    // Forward snaps always run the full SNAP_DURATION so the sweep into the
    // header is never perceived as a jump. Backward snaps scale with distance
    // (a small rebound should feel quick) but never drop below the minimum.
    if (target >= VIRTUAL_MAX) {
      snapActiveDuration = SNAP_DURATION;
    } else {
      var fraction = Math.abs(target - virtualY) / VIRTUAL_MAX;
      snapActiveDuration = Math.max(SNAP_DURATION_MIN, SNAP_DURATION * fraction);
    }
    rafId = requestAnimationFrame(renderSnap);
  }

  // ---- Finish ----

  function finish() {
    done = true;
    sessionStorage.setItem('logo-intro-seen', '1');
    releaseInteractions();
    adoptLettersIntoHeader();
    clearAutoTimer();
    clearTimeout(wheelTimer);
    removeListeners();
  }

  function adoptLettersIntoHeader() {
    letters.forEach(function (el) { el.style.transform = ''; });
    lettersCon.style.padding = '0';
    headerEl.innerHTML = '';
    headerEl.appendChild(lettersCon);
    overlay.setAttribute('data-done', '');
    document.body.classList.remove('logo-intro-active');
  }

  // ---- Interaction release ----

  function releaseInteractions() {
    if (interactionsReleased) return;
    interactionsReleased = true;
    unlockScroll();
    overlay.style.pointerEvents = 'none';
  }

  // ---- Scroll lock ----

  function lockScroll() {
    document.documentElement.classList.add('logo-intro-scroll-lock');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow            = 'hidden';
  }

  function unlockScroll() {
    document.documentElement.classList.remove('logo-intro-scroll-lock');
    document.documentElement.style.overflow = '';
    document.body.style.overflow            = '';
  }

  // ---- Auto-trigger timer ----

  function clearAutoTimer() {
    if (autoTimer) { clearTimeout(autoTimer); autoTimer = null; }
  }

  function resetAutoTimer() {
    clearAutoTimer();
    if (!done) {
      autoTimer = setTimeout(function () {
        startSnap(VIRTUAL_MAX);
      }, AUTO_TRIGGER_DELAY);
    }
  }

  // ---- Snap-on-release helpers ----

  function snapOnRelease() {
    if (done) return;
    var progress = virtualY / VIRTUAL_MAX;
    startSnap(progress >= SNAP_THRESHOLD ? VIRTUAL_MAX : 0);
    resetAutoTimer();
  }

  // ---- Input handlers ----

  function onWheel(e) {
    if (done) return;
    e.preventDefault();

    // Cancel any in-progress snap
    if (isSnapping) {
      isSnapping = false;
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
    }

    var capped = Math.max(-DELTA_CAP, Math.min(DELTA_CAP, e.deltaY));
    virtualY = Math.max(0, Math.min(VIRTUAL_MAX, virtualY + capped));
    scheduleScrub();

    // Detect wheel-end via idle timeout
    clearTimeout(wheelTimer);
    wheelTimer = setTimeout(snapOnRelease, 180);

    resetAutoTimer();
  }

  var touchStartY  = 0;
  var touchMoved   = false;

  function onTouchStart(e) {
    if (done) return;
    touchStartY = e.touches[0].clientY;
    touchMoved  = false;

    // Cancel snap so user can scrub back
    if (isSnapping) {
      isSnapping = false;
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
    }

    // Pause auto-timer while finger is down — it restarts on touchend
    clearAutoTimer();
  }

  function onTouchMove(e) {
    if (done) return;
    e.preventDefault();
    var dy = touchStartY - e.touches[0].clientY;
    touchStartY = e.touches[0].clientY;
    var cappedDy = Math.max(-DELTA_CAP, Math.min(DELTA_CAP, dy));
    virtualY = Math.max(0, Math.min(VIRTUAL_MAX, virtualY + cappedDy));
    touchMoved = true;
    scheduleScrub();
  }

  function onTouchEnd() {
    if (done) return;
    // Tap with no scroll movement — treat as a click and complete the animation
    if (!touchMoved) {
      startSnap(VIRTUAL_MAX);
      return;
    }
    snapOnRelease();
  }

  // Non-scrubbing interactions: instantly complete the animation
  function onKeyDown() {
    if (done) return;
    startSnap(VIRTUAL_MAX);
  }

  function onPointerDown(e) {
    if (done) return;
    // Touch is handled by touchstart/touchmove/touchend for scrubbing
    if (e.pointerType === 'touch') return;
    startSnap(VIRTUAL_MAX);
  }

  function onResize() {
    if (done) return;
    letters.forEach(function (el) { el.style.transform = ''; });
    lettersCon.style.opacity = '';
    logoWrap.style.transform = 'translateY(-50%)';
    calcDeltas();
    applyProgress(virtualY / VIRTUAL_MAX);
  }

  // ---- Cleanup ----

  function removeListeners() {
    window.removeEventListener('wheel',        onWheel);
    window.removeEventListener('touchstart',   onTouchStart);
    window.removeEventListener('touchmove',    onTouchMove);
    window.removeEventListener('touchend',     onTouchEnd);
    window.removeEventListener('keydown',      onKeyDown);
    window.removeEventListener('pointerdown',  onPointerDown);
    window.removeEventListener('resize',       onResize);
  }

  // ---- Bootstrap ----

  document.body.classList.add('logo-intro-active');
  lockScroll();

  function init() {
    requestAnimationFrame(function () {
      calcDeltas();
      applyProgress(0);
      resetAutoTimer();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('wheel',       onWheel,      { passive: false });
  window.addEventListener('touchstart',  onTouchStart, { passive: true  });
  window.addEventListener('touchmove',   onTouchMove,  { passive: false });
  window.addEventListener('touchend',    onTouchEnd,   { passive: true  });
  window.addEventListener('keydown',     onKeyDown,    { passive: true  });
  window.addEventListener('pointerdown', onPointerDown,{ passive: true  });
  window.addEventListener('resize',      onResize,     { passive: true  });
})();
